/**
 * Apply rotate / crop / color filters to an image File via canvas.
 * Output is always JPEG (GIFs should skip this path to preserve animation).
 */

import { buildColorCssFilter } from './mediaColorFilter';

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

function normalizeRotation(rotation) {
  const r = Number(rotation) || 0;
  return ((Math.round(r / 90) * 90) % 360 + 360) % 360;
}

/**
 * Centered crop rect in source pixels after optional aspect + zoom.
 * @param {number} srcW
 * @param {number} srcH
 * @param {number|null} aspect — width/height ratio, or null for full frame
 * @param {number} zoom — >= 1
 */
export function computeCenterCropRect(srcW, srcH, aspect = null, zoom = 1) {
  let cropW = srcW;
  let cropH = srcH;
  if (aspect && aspect > 0) {
    if (srcW / srcH > aspect) {
      cropW = srcH * aspect;
      cropH = srcH;
    } else {
      cropW = srcW;
      cropH = srcW / aspect;
    }
  }
  const z = Math.max(1, Number(zoom) || 1);
  cropW /= z;
  cropH /= z;
  return {
    x: (srcW - cropW) / 2,
    y: (srcH - cropH) / 2,
    width: cropW,
    height: cropH,
  };
}

/**
 * @param {File|Blob} file
 * @param {object} [edits]
 * @param {number} [edits.rotation] — 0 | 90 | 180 | 270
 * @param {number} [edits.brightness] — -50..50 (0 = unchanged)
 * @param {number} [edits.contrast] — -50..50 (0 = unchanged)
 * @param {number} [edits.saturation] — -50..50 (0 = unchanged)
 * @param {number} [edits.warmth] — -40..40 (0 = unchanged; + warm / − cool)
 * @param {number|null} [edits.aspect] — e.g. 1, 4/3, 16/9, or null
 * @param {number} [edits.zoom] — 1..3
 * @returns {Promise<File>}
 */
export async function applyImageEdits(file, {
  rotation = 0,
  brightness = 0,
  contrast = 0,
  saturation = 0,
  warmth = 0,
  aspect = null,
  zoom = 1,
} = {}) {
  if (!file) throw new Error('No image selected.');

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('Could not read that image for editing.');
  }

  try {
    const rot = normalizeRotation(rotation);
    const rad = (rot * Math.PI) / 180;
    const swap = rot === 90 || rot === 270;
    const rotatedW = swap ? bitmap.height : bitmap.width;
    const rotatedH = swap ? bitmap.width : bitmap.height;

    const rotated = document.createElement('canvas');
    rotated.width = rotatedW;
    rotated.height = rotatedH;
    const rctx = rotated.getContext('2d');
    if (!rctx) throw new Error('Could not edit image in this browser.');

    rctx.translate(rotatedW / 2, rotatedH / 2);
    rctx.rotate(rad);
    rctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);

    const crop = computeCenterCropRect(rotatedW, rotatedH, aspect, zoom);
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(crop.width));
    out.height = Math.max(1, Math.round(crop.height));
    const octx = out.getContext('2d');
    if (!octx) throw new Error('Could not edit image in this browser.');

    octx.filter = buildColorCssFilter({ brightness, contrast, saturation, warmth });
    octx.drawImage(
      rotated,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      out.width,
      out.height
    );
    octx.filter = 'none';

    const blob = await canvasToBlob(out, 'image/jpeg', 0.92);
    if (!blob) throw new Error('Could not save edited image.');

    const base = String(file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${base}-edited.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close();
  }
}

export const IMAGE_EDIT_DEFAULTS = {
  rotation: 0,
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
  aspect: null,
  zoom: 1,
};

export default applyImageEdits;
